import {
  AccessRole,
  AppAbility,
  createSalonAbilityForUser,
  FranchiseAccess,
  UserContext,
} from "@salon/types/authorization";
import { useMemo } from "react";
import { User, useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";

/**
 * Helper to create UserContext from frontend auth state
 */
function createUserContextFromAuth(
  user: User | null,
  franchiseAccesses: FranchiseAccess[] | null
): UserContext | null {
  if (!user) {
    return null;
  }

  return {
    userId: user.id,
    email: user.email,
    role: user.role,
    franchiseAccesses: franchiseAccesses ?? [],
  };
}

/**
 * Hook to get the current user's ability for the current salon
 * Returns AppAbility that can be used with ability.can(action, subject)
 *
 * Now uses full franchise access data from the refactored salon store
 */
export function useSalonAbility(): AppAbility {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const franchiseAccess = useSalonStore((state) => state.franchiseAccess);

  return useMemo(() => {
    if (!isAuthenticated || !user || !currentSalon) {
      return createSalonAbilityForUser(null, "");
    }

    // Convert FullFranchiseAccess from GraphQL to FranchiseAccess format for authorization service
    // This mapping ensures we have all the necessary access data for permission checking
    const franchiseAccesses: FranchiseAccess[] = franchiseAccess.map((fa) => ({
      franchiseId: fa.franchiseId,
      role: fa.role as AccessRole, // GraphQL role string to AccessRole type
      isActive: fa.isActive,
      salons: fa.franchise.salons.map((salon) => ({
        salonId: salon.id, // FullSalonAccess.id -> SalonAccess.salonId
        role: salon.role as AccessRole, // GraphQL role string to AccessRole type
        isActive: salon.isActive,
      })),
    }));

    const userContext = createUserContextFromAuth(user, franchiseAccesses);
    return createSalonAbilityForUser(userContext, currentSalon.id);
  }, [isAuthenticated, user, currentSalon, franchiseAccess]);
}
